import { buildShareText } from "./shareUrl";
import {
  trackScoreShareCancelled,
  trackScoreShareFailed,
  trackScoreShareLinkCopied,
  trackScoreShareRequested,
  trackScoreShareSucceeded,
} from "@/analytics/gameAnalytics";
import type { ShareContext } from "@/analytics/events";

export type NativeShareOutcome = "shared" | "cancelled" | "failed" | "unsupported";
export type CopyLinkOutcome = "copied" | "copy_failed";

/**
 * Whether the Web Share API is available at all - used both to decide
 * whether the share modal offers a SHARE button and, in tests, to keep
 * that decision out of the modal's own rendering logic. Never itself
 * invokes `navigator.share()`.
 */
export function canUseNativeShare(): boolean {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  return !!nav && typeof nav.share === "function";
}

async function doNativeShare(text: string, url: string): Promise<NativeShareOutcome> {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  if (!nav || typeof nav.share !== "function") return "unsupported";
  try {
    await nav.share({ title: "SnakeScore", text, url });
    return "shared";
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") return "cancelled";
    return "failed";
  }
}

/**
 * Writes ONLY the canonical URL to the clipboard - never the share text,
 * a title, the score, or any surrounding whitespace. This is deliberate:
 * WhatsApp/Discord/iMessage/etc. all generate their own rich preview (the
 * existing `score-challenge-meta.ts` Open Graph/Twitter Edge Function)
 * from a bare URL - prepending the share message here would either break
 * that unfurl or duplicate the message the receiving app already renders.
 * SHARE (native, `doNativeShare` above) is the one path that still sends
 * the fuller `text` + `url` together, since the OS share sheet itself
 * decides how to combine them for whatever target app the player picks.
 */
async function doCopyLink(url: string): Promise<CopyLinkOutcome> {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  try {
    if (!nav?.clipboard?.writeText) throw new Error("Clipboard API unavailable.");
    await nav.clipboard.writeText(url);
    return "copied";
  } catch {
    return "copy_failed";
  }
}

/**
 * Invoked ONLY when the player explicitly taps SHARE inside the in-game
 * "CHALLENGE A FRIEND" modal (see `ShareChallengeModal.ts`/
 * `ShareChallengeController.ts`) - never automatically on modal open, and
 * never chained into from a clipboard failure. This is the fix for the
 * round-5 UX bug: the OS share sheet must be an explicit choice, not the
 * only path, and a cancellation/failure here must never silently fall
 * through to the clipboard - the modal stays open either way so the player
 * can retry or choose COPY LINK themselves.
 *
 * `score_share_requested` fires here (the explicit-choice moment), not on
 * modal open - matching the same "never before an explicit share" analytics
 * contract as `score_share_succeeded`/`cancelled`/`failed`, all three of
 * which describe the native share outcome specifically (a clipboard copy
 * has its own dedicated `score_share_link_copied` event, fired by
 * `performCopyLink` below).
 */
export async function performNativeShare(context: ShareContext, dailyPoints: number, url: string): Promise<NativeShareOutcome> {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  if (!nav || typeof nav.share !== "function") return "unsupported";

  trackScoreShareRequested(context);
  const outcome = await doNativeShare(buildShareText(dailyPoints), url);
  if (outcome === "shared") trackScoreShareSucceeded(context, "native_share");
  else if (outcome === "cancelled") trackScoreShareCancelled(context);
  else if (outcome === "failed") trackScoreShareFailed(context, "native_share");
  return outcome;
}

/**
 * Invoked ONLY when the player explicitly taps COPY LINK. Writes ONLY the
 * bare URL to the clipboard (see `doCopyLink`'s doc) and fires
 * `score_share_link_copied` on confirmed success only - never claims a
 * copy succeeded just because it was attempted, and never fires the
 * native-share-only `score_share_succeeded`/`cancelled` events.
 */
export async function performCopyLink(context: ShareContext, url: string): Promise<CopyLinkOutcome> {
  const outcome = await doCopyLink(url);
  if (outcome === "copied") trackScoreShareLinkCopied(context);
  else trackScoreShareFailed(context, "clipboard");
  return outcome;
}

/**
 * QA-fixture variants: perform the exact same real browser Share/Clipboard
 * interaction (so a QA preview genuinely exercises the OS share sheet and
 * clipboard, not a fake stub) but fire NONE of the `score_share_*` PostHog
 * events - a QA preview must never contaminate real share-funnel data, the
 * same invariant `scoreChallengeQa.ts` already documents for
 * `score_challenge_*`.
 */
export async function performNativeShareNoTracking(dailyPoints: number, url: string): Promise<NativeShareOutcome> {
  return doNativeShare(buildShareText(dailyPoints), url);
}

export async function performCopyLinkNoTracking(url: string): Promise<CopyLinkOutcome> {
  return doCopyLink(url);
}
